import AppRenderer from 'lib-node-amb-renderer';
import { Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';

import componentMapper from '~/common/componentMapper';
import { KYLTableClass } from '~/common/componentMapper/ChakraTable/KYLTable';
import { KYLTableRowClass } from '~/common/componentMapper/ChakraTable/KYLTableRow';
import BlockedPage from '~/common/components/BlockedPage';
import Box from '~/common/components/Box';
import { useNavFeatureFlagsQuery } from '~/common/hooks/useNavFeatureFlags';

import Spinner from '../../GlobalLayout/Spinner';
import Oops from '../../Oops';
import { useKnowYourLoanSummaryQuery } from '../hooks';
import layout from '../layout.yaml?raw';

const ActionBar = lazy(
  () => import('~/common/components/GlobalLayout/ActionBar/ActionBar'),
);

const customComponents = {
  KYLTableRow: KYLTableRowClass,
  KYLTable: KYLTableClass,
};

type KYLFormTemplateProps = {
  schema?: any;
  formFields?: any;
};

function KYLFormTemplate({ formFields }: Readonly<KYLFormTemplateProps>) {
  return (
    <Box sx={{ padding: '2rem' }}>
      {formFields}
      <Suspense fallback={null}>
        <ActionBar
          primaryAction={{
            isDisabled: true,
          }}
          waitForSignalR
        />
      </Suspense>
    </Box>
  );
}

export default function BorrowerSummary() {
  const {
    isLoading,
    data: { IncludeKnowYourLoan },
  } = useNavFeatureFlagsQuery();

  if (isLoading) {
    return <Spinner />;
  }

  if (!IncludeKnowYourLoan) {
    return <BlockedPage />;
  }

  return <BorrowerSummaryPage />;
}

function BorrowerSummaryPage() {
  const { category } = useParams<'category'>();
  const isPrimaryBorrowerTab = category === 'primary';

  const { data, isLoading } = useKnowYourLoanSummaryQuery();

  const borrowerData = data?.screenData?.borrowers?.find((borrower) => {
    if (isPrimaryBorrowerTab && borrower.isPrimaryBorrower) {
      return borrower;
    }

    if (category === borrower.customerRecordId?.toString()) {
      return borrower;
    }
  });

  if (!borrowerData && isLoading) {
    return <Spinner />;
  }

  if (!borrowerData && !isLoading) {
    return <Oops />;
  }

  return (
    <AppRenderer
      markup={layout}
      onSubmit={() => {}}
      data={borrowerData}
      componentMapper={componentMapper}
      FormTemplate={KYLFormTemplate}
      LoadingComponent={<Spinner />}
      customComponents={customComponents}
    />
  );
}
